/**
 * v0 by Vercel.
 * @see https://v0.dev/t/Uj8S2aGtkAS
 * Documentation: https://v0.dev/docs#integrating-generated-code-into-your-nextjs-app
 */
"use client";

import { useEffect, useState } from "react";
import { ArrowLeft } from "@phosphor-icons/react";
import { SearchBoxV2 } from "@/components/SearchBoxV2";
import { subMonths } from "date-fns";
import { sqmToSqft, sqmToSqyd } from "@/app/utils/area";

export type PriceSubmissionForm = {
  inputBlob?: string;
  propertyType?: "plots" | "apartments";
  pricePerSqm?: number;
  price?: number;
  area?: number;
  projectId?: string;
  projectName?: string;
  projectLocation?: string;
  locationId?: number;
  quoteDate?: Date;
};

export default function Component() {
  const [currentPage, setCurrentPage] = useState(0);
  const [priceUnit, setPriceUnit] = useState("Lakh"); // ["Lakh", "Crore"
  const [priceEntry, setPriceEntry] = useState(0);
  const [areaEntry, setAreaEntry] = useState(0);
  const [isInputValid, setIsInputValid] = useState(false);
  const [formData, setFormData] = useState<PriceSubmissionForm>({});
  const nextPage = () => {
    setCurrentPage(currentPage + 1);
  };
  const handleProjectName = ({
    text,
    projectName,
    villageName,
    projectId,
    villageId,
  }: {
    text?: string;
    projectName?: string;
    villageName?: string;
    projectId?: string;
    villageId?: number;
  }) => {
    setFormData({
      ...formData,
      projectName,
      projectLocation: villageName,
      inputBlob: text,
      projectId: projectId,
      locationId: villageId,
    });
  };

  const handleAgeOfQuote = (e: "3months" | "moreThan3Months") => {
    const quoteDate = e === "3months" ? new Date() : subMonths(new Date(), 3);
    setFormData({
      ...formData,
      quoteDate,
    });
    nextPage();
  };
  const handlePropertyType = (e: PriceSubmissionForm["propertyType"]) => {
    setFormData({
      ...formData,
      propertyType: e,
    });
    nextPage();
  };
  const handlePrevPage = () => {
    setCurrentPage(currentPage - 1);
  };

  useEffect(() => {
    console.log(formData);
  }, [formData]);

  // use this to update page on submit
  useEffect(() => {
    if(currentPage === 4) {
      const { price, area} = formData;
      if(price && area) {
        const pricePerSqm = price / area
        setFormData({
          ...formData,
          pricePerSqm
        })
      }
    } else if (currentPage === 5) {
      fetch("/api/price", {
        method: "POST",
        body: JSON.stringify(formData),
      }).then((res) => {
        console.log(res);
      });
    }
  }, [currentPage]);

  useEffect(() => {
    const fullPrice =
      priceUnit === "Lakh" ? priceEntry * 100000 : priceEntry * 10000000;
    setFormData({
      ...formData,
      price: fullPrice,
    });
    console.log(`fullPrice = ${fullPrice}`);
  }, [priceEntry, priceUnit]);

  useEffect(() => {
    const area =
      formData.propertyType === "plots"
        ? areaEntry / sqmToSqyd(1)
        : areaEntry / sqmToSqft(1);
    setFormData({
      ...formData,
      area
    });
  }, [areaEntry]);
  // Use this to update state on page
  useEffect(() => {
    if (currentPage === 2) {
      if (
        !formData.projectName &&
        !formData.projectLocation &&
        !formData.inputBlob
      ) {
        setIsInputValid(false);
      } else {
        setIsInputValid(true);
      }
    } else if (currentPage === 3) {
      if (!formData.price || !formData.area) {
        setIsInputValid(false);
      } else {
        setIsInputValid(true);
      }
    } else {
      setIsInputValid(true);
    }
  }, [formData, currentPage]);

  return (
    <div className="max-w-2xl mx-auto p-2 min-h-96">
      <form method="dialog">
        <button className="btn btn-xs btn-circle btn-ghost absolute right-2 top-2">
          ✕
        </button>
      </form>
      <div className="">
        <div className="flex justify-between mb-4 flex-col gap-2">
          <progress
            className="progress progress-secondary"
            value={currentPage}
            max="4"
          ></progress>
        </div>
        {currentPage === 0 && (
          <div className="flex flex-col gap-4">
            <h3 className="text-2xl font-bold">Share prices, support buyers</h3>
            <div className="text-sm text-gray-500">
              Sharing your quotes helps everyone find the right price!
            </div>
            <p className="text-sm text-gray-500">
              If you have recieved a quote for a plot, apartment or villa in the
              past, please share it here. It only takes 30 seconds !
            </p>
          </div>
        )}
        {currentPage === 1 && (
          <div>
            <div className="grid gap-4">
              <div className="form-control flex flex-col gap-2">
                <div
                  className="btn btn-primary text-wrap"
                  onClick={() => handlePropertyType("plots")}
                >
                  I have a quote for a plot
                </div>
                <div
                  className="btn btn-primary text-wrap"
                  onClick={() => handlePropertyType("apartments")}
                >
                  I have a quote for an apartment/villa
                </div>
              </div>
            </div>
          </div>
        )}
        {currentPage === 2 && (
          <div className="w-full">
            <SearchBoxV2
              showGeoDisclosures={false}
              placeHolderText={["Enter the locality, village or project name"]}
              onTextChange={handleProjectName}
              useLinksForResults={false}
            />
          </div>
        )}
        {currentPage === 3 && (
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-row gap-4 justify-between">
              <label className="border-solid flex items-center gap-2 text-gray-500">
                Area{" "}
                {formData.propertyType === "plots" ? "(Sq. Yards)" : "(Sq. Ft)"}
              </label>
            </div>
            <div className="flex flex-row join col-span-2">
              <div>
                <div>
                  <input
                    type="number"
                    value={areaEntry}
                    onChange={(e) => setAreaEntry(parseInt(e.target.value))}
                    className="input input-bordered text-gray-800 join-item no-spinners"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-4">
              <div className="flex flex-row gap-4  justify-between">
                <label className="border-solid flex items-center gap-2 text-gray-500">
                  Price (₹)
                </label>
              </div>
            </div>
            <div className="flex flex-row join col-span-2">
              <div>
                <div>
                  <input
                    type="number"
                    value={priceEntry}
                    onChange={(e) => setPriceEntry(parseInt(e.target.value))}
                    className="input max-w-36 input-bordered join-item text-gray-800 no-spinners"
                  />
                </div>
              </div>
              <select
                className="select select-bordered join-item"
                value={priceUnit}
                onChange={(e) => setPriceUnit(e.target.value)}
              >
                <option value="Lakh">Lakhs</option>
                <option value="Crore">Crores</option>
              </select>
            </div>
          </div>
        )}
        {currentPage === 4 && (
          <div className="flex flex-col gap-4">
            <div className="w-full flex justify-center items-center gap-2 text-gray-500 font-semibold">
              When did you get this quote?
            </div>
            <div className="form-control flex flex-col gap-2">
              <div
                className="btn btn-primary text-wrap"
                onClick={() => handleAgeOfQuote("3months")}
              >
                In the last 3 months
              </div>
              <div
                className="btn btn-primary text-wrap"
                onClick={() => handleAgeOfQuote("moreThan3Months")}
              >
                More than 3 months ago
              </div>
            </div>
          </div>
        )}
        {currentPage === 5 && (
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-xl font-bold mb-2">Thank you.</h3>
            <div>
              Your contribution helps all buyers find the right price. 🙏
            </div>
            <div className="text-4xl">🎉 👍</div>
            <form method="dialog">
              <button className="btn btn-primary min-w-36">Close</button>
            </form>
          </div>
        )}
        {currentPage < 5 && (
          <div className="flex justify-between mt-6">
            {[1, 2, 3, 4].includes(currentPage) && (
              <div onClick={handlePrevPage} className="btn btn-ghost min-w-24">
                <ArrowLeft size={12} />
                Previous
              </div>
            )}
            {[0, 2, 3].includes(currentPage) && (
              <div
                onClick={() => nextPage()}
                className={`btn btn-primary min-w-24 ${
                  isInputValid ? "" : "btn-disabled"
                }`}
                aria-disabled={!isInputValid}
              >
                Next
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
